import {
  B2bWithdrawalOrdersFlow,
  B2bWithdrawalStatus,
  B2bWithdrawalType,
  GetB2bWithdrawalsResponse,
} from '@Common/types/bo/b2b/withdrawals';
import { B2BEarlyWithdrawalReason } from '@Common/types/funding/withdraw_early_request';
import { MdlrPaymentStatus } from '@Common/types/modulr';

const withdrawalFields: Record<
  | keyof GetB2bWithdrawalsResponse[number]
  | 'orders'
  | 'b2b_company_plan_ids'
  | 'b2b_campaign_id'
  | 'savings_funds_requests_id'
  | 'b2b_flags'
  | 'assets_class',
  string
> = {
  id: 'Withdrawal ID',
  user_id: 'User ID',
  company_id: 'Company ID',
  contract_id: 'Contract ID',
  investing_product: 'Investing Product',
  assets_class: 'Assets Class',
  b2b_company_plan_ids: 'Company Plan IDs',
  b2b_campaign_id: 'Campaign ID',
  mdlr_beneficiary_id: 'Modulr Beneficiary ID',
  savings_funds_requests_id: 'Funds Request ID',
  account_type: 'Account Type',
  sort_code: 'Sort Code',
  account_number: 'Account Number',
  bank_country: 'Bank Domiciliation Country',
  status: 'Status',
  type: 'Type',
  orders_flow: 'Orders Flow',
  is_planned_withdrawal: 'Planned Withdrawal',
  gross_amount: 'Gross Amount',
  net_amount: 'Net Amount',
  tax_amount: 'Tax Amount',
  initial_amount: 'Initial Amount',
  orders: 'Orders',
  currency: 'Currency',
  task_state: 'Task State',
  task_assignee_group: 'Assigned To',
  task_assignee: 'Assignee',
  bo_task_id: 'Task ID',
  early_withdraw_reason: 'Early withdrawal reason',
  early_withdraw_event_date: 'Early withdrawal event date',
  employee_id: 'Employee ID',
  created_at: 'Created At',
  paid_at: 'Paid at',
  payment_status: 'Payment Status',
  updated_at: 'Updated At',
  correlation_id: 'Correlation ID',
  b2b_flags: 'User B2b flags',
  firstname: 'First Name',
  lastname: 'Last Name',
};

const bankAccountTypes = {
  iban: 'IBAN',
  intl: 'International',
  scan: 'SCAN (UK)',
  dd: 'Direct Debit',
};

export const b2bWithdrawalsCreateTranslations = {
  title: 'Create a withdrawal request',
  steps: {
    1: 'General information',
    2: 'Amounts',
    3: 'Payment',
    4: 'Documents',
  },
  sections: {
    affiliate_information: 'Affiliate information',
    from: 'Withdrawal request from',
    payment: 'Payment',
    documents_identity: 'User ID Documents',
    documents_supporting: 'Supporting Documents',
  },
  suspended: {
    title: 'Funds on suspended instruments: %{total}',
    description:
      'These funds belong to suspended instruments and cannot be withdrawn until the instruments are reactivated.',
    not_withdrawable: 'All available funds belong to suspended instruments and cannot be withdrawn',
  },
  subtitles: {
    from: 'Select from which Plan you want to withdraw, or start with early withdrawal request',
    available: '%{available} € available',
    available_out: '%{available} € available today out of %{total} €',
    available_locked: '%{available} € available after unlock',
  },
  errors: {
    failure: 'Unable to create withdrawal request: %{error}',
    no_employees_found: 'Employee not found',
    pending_orders: 'Cannot create withdrawal request with pending orders',
    deceased_requires_third_party:
      'A withdrawal for a deceased saver must be paid to a third-party bank account',
    insufficient_balance: 'Insufficient balance',
    no_beneficiaries: 'No bank accounts found for this employee or company',
    duplicate_employee_name:
      'Another employee has the same first name and last name. Please make sure you have selected the correct employee.',
    duplicate_employee_row: 'Employee ID %{id} - %{company} - NIR %{nir}',
  },
  labels: {
    b2b_employee_id_search: 'Search with Employee ID / Full name / NIR / Registration number',
    available_funds: 'Available funds',
    early_request: 'Request early withdrawal',
    amount: 'Amount to withdraw',
    document_identity_still_valid: 'Make sure the ID document is still valid',
    date_of_expiry: 'Date of expiry',
    date_of_event: 'Date of event',
    reason: 'Reason of request',
    no_funds: 'No products with funds found',
    amount_to_withdraw:
      'Net amount to withdraw after social contributions on the capital gain: EUR %{amount}',
    amounts_estimations_notice:
      'The amounts are estimated based on the latest known net asset values (NAVs). The final amount may change due to sell orders at an unknown price.',
    select_bank_account: 'Select bank account',
    employee_account_option: 'Employee account - %{iban} - %{name}',
    company_account_option: 'Company account - %{iban} - %{name}',
    third_party_account_option: 'Third-party bank account',
    third_party_details: 'Enter third-party bank account details',
    third_party_only_deceased:
      'The saver is deceased or the reason is saver death. The withdrawal must be paid to a third-party bank account.',
    iban: 'IBAN',
    bic: 'BIC',
    rib_upload: 'RIB document (PDF)',
    account_type: 'Account type',
    account_type_iban: 'IBAN account',
    account_type_intl: 'International account',
    account_type_scan: 'SCAN account (UK)',
    sort_code: 'Sort Code',
    account_number: 'Account number',
    entity_type: 'Beneficiary type',
    entity_individual: 'Individual',
    entity_institution: 'Institution',
    first_name: 'First name',
    last_name: 'Last name',
    company_name: 'Company name',
    beneficiary_address: 'Beneficiary address',
    address_street: 'Street',
    address_city: 'City',
    address_postcode: 'Postal code',
    address_country: 'Country',
  },
  validation: {
    date_of_expiry: 'Date of expiry must not be in the past',
    non_zero_allocation_required: 'Total withdrawal amount must be greater than 0',
    beneficiary_required: 'Please select a bank account',
    third_party_required: 'Please fill in all third-party bank account fields',
    scan_sort_code_invalid: 'Sort code must contain exactly 6 digits',
    scan_account_number_invalid: 'Account number must contain exactly 8 digits',
    no_eligible_allocations: 'No eligible allocations found for given reason and date',
  },
};

export const b2bWithdrawalsTranslations = {
  fields: withdrawalFields,
  bank_account_types: bankAccountTypes,
  title: {
    show: 'Withdrawal',
  },
  cancel: {
    label: 'Cancel',
    success: 'Withdrawal cancelled successfully',
    failure: 'Failed to cancel withdrawal: %{error}',
    confirm_title: 'Cancel this withdrawal?',
    confirm_content:
      'The withdrawal will be cancelled along with its funds request #%{savings_funds_request_id} and its CCB sell orders. The employee CCB position is left unchanged.',
    reason_label: 'Reason',
    description_label: 'Description (optional)',
    confirm: 'Confirm cancellation',
    disabled_reasons: {
      unsupported_assets_class: 'Only CCB withdrawals can be cancelled here',
      not_approved: 'Only approved withdrawals can be cancelled',
      already_cancelled: 'This withdrawal is already cancelled',
      payout_payment_exists: 'A payout has already been sent for this withdrawal',
    },
  },
  orders: {
    link: 'Link',
  },
  sections: {
    details: 'Details',
    early: 'Early withdrawal',
    documents_requests: 'Request documents',
    ccb_instructions:
      'Approving this withdrawal will result in fund request to the company. It should be processed in order to process the withdrawal.',
    mdlr_beneficiary_id_missing:
      "This withdrawal does not have a linked beneficiary. The payout will be sent to the user's active approved beneficiary.",
    destination_account_warning_high_risk_country:
      'The destination account is from a high-risk country',
    destination_account_warning_country_under_surveillance:
      'The destination account is from a country under surveillance',
    documents_identity: 'Identity documents',
    b2b_proof_identity: 'B2B Proof of Identity tasks',
    documents_identity_upload: 'Upload an identity document',
    same_withdrawal_request: 'From same withdrawal request',
    linked_transfers: 'Linked Transfers',
  },
  create: b2bWithdrawalsCreateTranslations,
  bulk_create: {
    steps: {
      1: 'General Information',
      2: 'Review',
      3: 'Confirmation',
    },
    general_info: {
      header_1: '1. Add the ISIN code of the fund at maturity',
      header_2: '2. Download the template',
      header_3: '3. Complete your file',
      header_4: '4. Upload your completed file',
      complete_file_description:
        'Add your data to the file by following the guide. Run controls to make sure all everything is correct.',
    },
    confirmation: {
      title: 'Summary and confirmation',
      operation_type: 'Operation type: Withdrawal',
      destination_account: 'Destination account',
      total_gross_amount_withdrawal: 'Total gross amount withdrawal : %{amount} EUR',
      number_of_employees_concerned: 'Number of employees concerned: %{count}',
      employee_accounts: 'The bank accounts of employees',
      company_account: 'Company account - %{iban} - %{name}',
      company_account_missing: 'Company account',
      status: {
        pending: 'Pending',
        missing: 'Missing',
      },
    },
    beneficiary_missing_modal: {
      title: 'Missing company beneficiary',
      body: 'This company has no beneficiary configured for the company payout account. Add a beneficiary before creating the bulk withdrawal.',
    },
    labels: {
      download_eligible_employees: 'Download the list of eligible employees',
      upload_filled_template: 'Upload the filled template (.xlsm)',
      no_rows_found: 'No eligible employee',
      total_shares: 'Total shares',
      current_position: 'Current position',
      company: 'International company',
      instrument: 'Instrument ISIN (at maturity)',
      review_step_description:
        'Review the positions and quantities before proceeding with the withdrawal. All pages in the table must be viewed.',
    },
    validation: {
      upload_file_required: 'Please upload a filled template file before continuing',
      no_rows: 'No employee rows available to submit',
      review_all_pages_and_load_data:
        'Please review all pages and wait for all employee positions to be loaded before continuing',
      deceased_employees:
        'Some employees are deceased. Remove them from the file or set their quantity to 0 before continuing, and create their withdrawal individually with a third-party bank account.',
    },
    errors: {
      select_company_and_instrument: 'Select company and instrument first',
      template_generation_failed: 'Failed to generate bulk template',
      parse_file_failed: 'Failed to parse uploaded template',
      load_positions_failed: 'Unable to load current positions for all employees',
      create_failed: 'Failed to create bulk withdrawal requests',
      deceased_employees:
        'The following employees are deceased and cannot be included in a bulk withdrawal. Remove them from the file or set their quantity to 0, and create their withdrawal individually with a third-party bank account:',
    },
    success: {
      created: 'Bulk withdrawal requests created',
      template_generated: 'Template generated',
    },
    success_modal: {
      title: 'Withdrawal request created successfully',
      description:
        'Your transaction has been created successfully. Withdrawals are being created. You can track it via your withdrawal dashboard.',
      cta: 'Understood',
    },
    title: 'Create a withdrawal request',
    button: 'Bulk Create',
  },
  inputs: {
    ccb_funding_payment_method: 'Payment method',
    ccb_funding_payment_method_helper_text:
      'CCB withdrawals are funded by the company. If SEPA Direct Debit is selected, the company will be charged when the withdrawal is approved.',
    ccb_funding_payment_method_sepa: 'SEPA Direct Debit',
    ccb_funding_payment_method_transfer: 'Bank transfer',
    user_message: "Explanation for the user in user's language",
  },
  choices: {
    status: {
      new: 'New',
      pending: 'Pending',
      completed: 'Completed',
      pending_approval: 'Pending Approval',
      cancelled: 'Cancelled',
      settled: 'Settled',
    } satisfies Record<B2bWithdrawalStatus, string>,
    orders_flow: {
      regular: 'Regular',
      trigger_nav: 'Trigger NAV',
      floor_nav: 'Floor NAV',
      floor_price: 'Floor price',
    } satisfies Record<B2bWithdrawalOrdersFlow, string>,
    type: {
      regular: 'Regular',
      early: 'Early',
    } satisfies Record<B2bWithdrawalType, string>,
    payment_status: {
      reprocessing: 'Reprocessing',
      cancelled: 'Cancelled',
      void: 'Void',
      returned: 'Returned',
      held: 'Held',
      unallocated: 'Unallocated',
      submitted: 'Submitted',
      screening_req: 'Screening Required',
      validated: 'Validated',
      pending_for_date: 'Pending for Date',
      pending_for_funds: 'Pending for Funds',
      ext_proc: 'Externally Processed',
      processed: 'Processed',
      reconciled: 'Reconciled',
      er_invalid: 'Error: Invalid',
      er_extconn: 'Error: External Connection',
      er_extsys: 'Error: External System',
      er_expired: 'Error: Expired',
      er_general: 'Error: General',
      er_batch: 'Error: Batch',
      ext_sent: 'Externally Sent',
      clearing: 'Clearing',
      held_in_suspense: 'Held in Suspense',
      shares_canceled: 'Shares Canceled',
    } satisfies Record<MdlrPaymentStatus, string>,
    early_reason: {
      marriage: 'Marriage',
      conclusion_of_a_pacs: 'Conclusion of a pacs',
      divorce_or_separation_or_dissolution_of_a_pacs_with_dependent_child_s:
        'Divorce or separation or dissolution of a pacs with dependent child(s)',
      birth_or_adoption_of_the_3rd_child_then_of_each_subsequent_child:
        'Birth or adoption of the 3rd child then of each subsequent child',
      invalidity: 'Invalidity',
      savers_death: 'savers death',
      death_of_the_pacs_spouse_or_partner: 'Death of the pacs spouse or partner',
      over_indebtedness: 'Over indebtedness',
      domestic_violence: 'Domestic violence',
      creation_or_takeover_of_a_business_by_the_saver_or_his_children_or_his_spouse_or_partner_of_pacs:
        'Creation or takeover of a business by the saver or his children or his spouse or partner of pacs',
      cessation_of_self_employed_activity: 'Cessation of self-employed activity',
      termination_of_the_employment_contract_or_activity_of_individual_entrepreneur:
        'Termination of the employment contract or activity of individual entrepreneur',
      expiration_of_unemployment_insurance_rights: 'Expiration of unemployment insurance rights',
      leaving_for_retirement: 'Leaving for retirement',
      acquisition_of_the_main_residence: 'Acquisition of the main residence',
      construction_of_the_main_residence: 'Construction of the main residence',
      expansion_of_the_main_residence: 'Expansion of the main residence',
      rehabilitation_following_a_natural_disaster: 'Rehabilitation following a natural disaster',
      no_employment_contract_or_corporate_mandate: 'No employment contract or corporate mandate',
      termination_of_the_social_mandate: 'Termination of the social mandate',
      energy_renovation: 'Energy renovation',
      acquisition_of_shares_in_a_production_cooperative:
        'Acquisition of shares in a Production Cooperative (SCOP)',
      judicial_liquidation_or_full_transfer_of_the_company:
        'Judicial liquidation or full transfer of the company',
      purchase_of_a_low_emission_vehicle: 'Purchase of a low-emission vehicle',
      caregiver_activity_exercised_by_the_holder_or_their_spouse_or_pacs_partner:
        'Caregiver activity exercised by the holder, or their spouse or Pacs partner',
      option_exercise: 'Option exercise',
      instrument_maturity: 'At maturity',
      indirect_management: 'Indirect management',
    } satisfies Record<B2BEarlyWithdrawalReason, string>,
  },
};
